import React, { SetStateAction, Dispatch } from 'react';
import $i18n from '@/i18n';
import { Dialog, Radio, Checkbox } from '@alifd/next';
import { Message } from '@b-design/fusion';
import { HasAigcRole, HasOperatorRole } from '@/utils/config';
import './index.less';

import Button from '@/components/Button';

const RadioGroup = Radio.Group;

interface SetAsModal {
  setAsVisible: boolean;
  setSetAsRole: Dispatch<SetStateAction<string[]>>;
  setAsRole: string | undefined;
  onClose: () => void;
  onOk: () => Promise<void>;
  type?: string;
  roleList?: string[];
}

const SetAsModal: React.FC<SetAsModal> = ({
  setAsVisible,
  setSetAsRole,
  setAsRole,
  onClose,
  onOk,
  type,
  roleList,
}) => {
  const onlyAigc = roleList?.includes('AIGC_DESIGNER') && !roleList?.includes('ADMIN');

  const RadioOptions = [
    {
      value: 'ADMIN',
      title: $i18n.get({
        id: 'aopf.pages.member.actionSetAsAdmin',
        dm: '管理员',
      }),
      desc: $i18n.get({
        id: 'aopf.pages.member.actionSetAsAdminDesc',
        dm: '管理所有应用程序的最高权限',
      }),
      disabled: onlyAigc,
    },
    {
      value: 'DEVELOPER',
      title: $i18n.get({
        id: 'aopf.pages.member.actionSetAsDeveloper',
        dm: '开发者',
      }),
      desc: $i18n.get({
        id: 'aopf.pages.member.actionSetAsDeveloperDesc',
        dm: '开发与测试应用',
      }),
      disabled: onlyAigc,
    },
    ...(HasOperatorRole() ? [{
      value: 'OPERATOR',
      title: $i18n.get({
        id: 'aopf.components.SetAsModal.OperationsAdministrator',
        dm: '运营管理员',
      }),
      desc: $i18n.get({
        id: 'aopf.component.addMemberModal.indexCp.HaveManagementPermissionsForMini',
        dm: '拥有小程序运营的管理权限',
      }),
    }] : []),
    ...(HasAigcRole()
      ? [
          {
            value: 'AIGC_DESIGNER',
            title: $i18n.get({ id: 'aopf.components.SetAsModal.AigcDesigner', dm: 'AIGC设计者' }),
            desc: $i18n.get({
              id: 'aopf.components.SetAsModal.PermissionToUseTheSmart',
              dm: '智绘平台使用权限',
            }),
            disabled: !roleList?.includes('AIGC_DESIGNER'),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Dialog
        v2
        title={$i18n.get({ id: 'aopf.pages.member.columnsActionSetAs', dm: '设置' })}
        visible={setAsVisible}
        onClose={onClose}
        footerActions={['cancel', 'ok']}
        className="modal-setAs"
        footer={[
          <div className="dialog-footer">
            <Button className="dialog-btn" type="normal" onClick={onClose}>
              {$i18n.get({ id: 'aopf.component.invite.Cancel', dm: '取消' })}
            </Button>
            <Button
              type="primary"
              onClick={async () => {
                if (!setAsRole?.length) {
                  Message.warning({
                    title: $i18n.get({
                      id: 'aopf.pages.member.addMemberRoleErrorTip',
                      dm: '请选择角色!',
                    }),
                  });
                  return;
                }
                await onOk();
              }}
            >
              {$i18n.get({ id: 'aopf.component.invite.Ok', dm: '确定' })}
            </Button>
          </div>,
        ]}
      >
        <p>{$i18n.get({ id: 'aopf.pages.member.columnsRole', dm: '角色' })}</p>
        {type === 'app' ? (
          <RadioGroup
            direction="ver"
            value={setAsRole?.[0]}
            onChange={(value) => {
              const _value = value ? [value] : [];
              setSetAsRole(_value as string[]);
            }}
            aria-labelledby="groupId"
            className="setAsRadio"
          >
            {RadioOptions.slice(0, 2).map((item) => (
              <Radio id={item?.value} value={item?.value} key={item?.value}>
                <span
                  className="roleTitle"
                  style={{ width: $i18n.language === 'zh-CN' ? '90px' : '130px' }}
                >
                  {item?.title}
                </span>
                <span className="roleDesc">{item?.desc}</span>
              </Radio>
            ))}
          </RadioGroup>
        ) : (
          <Checkbox.Group
            direction="ver"
            value={setAsRole}
            onChange={(value) => {
              setSetAsRole(value as string[]);
            }}
            aria-labelledby="groupId"
            className="setAsRadio"
          >
            {RadioOptions?.map((item) => (
              <Checkbox
                id={item?.value}
                value={item?.value}
                key={item?.value}
                disabled={item.disabled}
              >
                <span
                  className="roleTitle"
                  style={{ width: $i18n.language === 'zh-CN' ? '90px' : '130px' }}
                >
                  {item?.title}
                </span>
                <span className="roleDesc">{item?.desc}</span>
              </Checkbox>
            ))}
          </Checkbox.Group>
        )}
      </Dialog>
    </div>
  );
};

export default SetAsModal;
